import { compareByOrder } from "./orderKey.js";
import { DictionaryFields, stringifyDocument, toDictionaryRows } from "./dictionaryRows.js";
import { normalizeSettings } from "./normalizeSettings.js";
//#region ../erd-editor-schema/dist/storageForm.js
var SCHEMA_URL = "https://raw.githubusercontent.com/dineug/erd-editor/main/json-schema/schema.json";
var compare = (a, b) => a < b ? -1 : a > b ? 1 : 0;
var liveIds = (ids, entities) => [...new Set(ids)].filter((id) => Object.hasOwn(entities, id));
var toRecord = (entities, order = (a, b) => compare(a.id, b.id)) => Object.fromEntries(entities.sort(order).map((entity) => [entity.id, entity]));
function toDocumentSettings(settings) {
	const source = normalizeSettings(settings);
	const written = {
		originX: source.originX,
		originY: source.originY,
		zoomLevel: source.zoomLevel,
		show: source.show,
		database: source.database,
		databaseName: source.databaseName,
		canvasType: source.canvasType,
		language: source.language,
		tableNameCase: source.tableNameCase,
		columnNameCase: source.columnNameCase,
		bracketType: source.bracketType,
		relationshipDataTypeSync: source.relationshipDataTypeSync,
		columnOrder: source.columnOrder,
		maxWidthComment: source.maxWidthComment,
		lockSettings: source.lockSettings
	};
	if (source.ddlScripts) written.ddlScripts = source.ddlScripts;
	if (source.nameViewMode !== void 0) written.nameViewMode = source.nameViewMode;
	return written;
}
var holdsValue = (key, entity) => DictionaryFields[key].some((field) => {
	const value = Reflect.get(entity, field);
	return field !== "id" && typeof value === "string" && value.trim() !== "";
});
var textField = (key, value) => value === "" ? {} : { [key]: value };
var logicalNameField = (logicalName) => typeof logicalName === "string" && logicalName.trim() !== "" ? { logicalName } : {};
var writtenRows = (key, entities) => entities.length !== 0 ? toDictionaryRows(key, entities) : void 0;
var holdsEntries = (value) => Array.isArray(value) ? value.length !== 0 : value !== void 0 && Object.keys(value).length !== 0;
function withEntries(fields) {
	const held = Object.entries(fields).filter(([, value]) => holdsEntries(value));
	return held.length !== 0 ? Object.fromEntries(held) : void 0;
}
function toDocument(schema) {
	const doc = schema.doc ?? {};
	const collections = schema.collections ?? {};
	const tableEntities = collections.tableEntities ?? {};
	const tableColumnEntities = collections.tableColumnEntities ?? {};
	const relationshipEntities = collections.relationshipEntities ?? {};
	const indexEntities = collections.indexEntities ?? {};
	const indexColumnEntities = collections.indexColumnEntities ?? {};
	const memoEntities = collections.memoEntities ?? {};
	const tableGroupEntities = collections.tableGroupEntities ?? {};
	const tableGroupIds = liveIds(doc.tableGroupIds ?? [], tableGroupEntities);
	const keptGroups = new Set(tableGroupIds);
	const wordEntities = collections.wordEntities ?? {};
	const termEntities = collections.termEntities ?? {};
	const domainEntities = collections.domainEntities ?? {};
	const domainIds = liveIds(doc.domainIds ?? [], domainEntities).filter((id) => holdsValue("domainEntities", domainEntities[id]));
	const keptDomains = new Set(domainIds);
	const tableIds = liveIds(doc.tableIds ?? [], tableEntities);
	const keptTables = new Set(tableIds);
	const columnIdsOf = /* @__PURE__ */ new Map();
	const tables = tableIds.map((id) => {
		const table = tableEntities[id];
		const columnIds = liveIds(table.columnIds, tableColumnEntities);
		columnIdsOf.set(id, new Set(columnIds));
		return {
			id: table.id,
			...textField("name", table.name),
			...logicalNameField(table.logicalName),
			...textField("comment", table.comment),
			columnIds,
			...keptGroups.has(table.groupId) ? { groupId: table.groupId } : {},
			...table.nameDerived === true ? { nameDerived: true } : {},
			ui: {
				x: table.ui.x,
				y: table.ui.y,
				...textField("color", table.ui.color)
			}
		};
	});
	const columns = [...new Set(tables.flatMap((table) => table.columnIds))].map((id) => {
		const column = tableColumnEntities[id];
		return {
			id: column.id,
			tableId: column.tableId,
			...textField("name", column.name),
			...textField("comment", column.comment),
			...textField("dataType", column.dataType),
			...textField("default", column.default),
			options: column.options,
			...logicalNameField(column.logicalName),
			...keptDomains.has(column.domainId) ? { domainId: column.domainId } : {}
		};
	});
	const relationshipIds = liveIds(doc.relationshipIds ?? [], relationshipEntities).filter((id) => {
		const { start, end } = relationshipEntities[id];
		return keptTables.has(start.tableId) && keptTables.has(end.tableId);
	});
	const relationships = relationshipIds.map((id) => {
		const relationship = relationshipEntities[id];
		const { start, end } = relationship;
		return {
			id: relationship.id,
			relationshipType: relationship.relationshipType,
			onDelete: relationship.onDelete,
			onUpdate: relationship.onUpdate,
			start: {
				tableId: start.tableId,
				columnIds: start.columnIds
			},
			end: {
				tableId: end.tableId,
				columnIds: end.columnIds
			}
		};
	});
	const indexIds = liveIds(doc.indexIds ?? [], indexEntities).filter((id) => keptTables.has(indexEntities[id].tableId));
	const indexes = indexIds.map((id) => {
		const index = indexEntities[id];
		const columnIds = columnIdsOf.get(index.tableId);
		const indexColumnIds = liveIds(index.indexColumnIds, indexColumnEntities).filter((indexColumnId) => columnIds.has(indexColumnEntities[indexColumnId].columnId));
		return {
			id: index.id,
			...textField("name", index.name),
			tableId: index.tableId,
			indexColumnIds,
			unique: index.unique
		};
	});
	const indexColumns = [...new Set(indexes.flatMap((index) => index.indexColumnIds))].map((id) => {
		const indexColumn = indexColumnEntities[id];
		return {
			id: indexColumn.id,
			indexId: indexColumn.indexId,
			columnId: indexColumn.columnId,
			orderType: indexColumn.orderType
		};
	});
	const memoIds = liveIds(doc.memoIds ?? [], memoEntities);
	const memos = memoIds.map((id) => {
		const memo = memoEntities[id];
		const { ui } = memo;
		return {
			id: memo.id,
			...textField("value", memo.value),
			ui: {
				x: ui.x,
				y: ui.y,
				width: ui.width,
				height: ui.height,
				...textField("color", ui.color)
			}
		};
	});
	const tableGroups = tableGroupIds.map((id) => {
		const tableGroup = tableGroupEntities[id];
		const { ui } = tableGroup;
		return {
			id: tableGroup.id,
			...textField("name", tableGroup.name),
			...textField("color", tableGroup.color),
			ui: {
				x: ui.x,
				y: ui.y,
				width: ui.width,
				height: ui.height
			}
		};
	});
	const words = liveIds(doc.wordIds ?? [], wordEntities).map((id) => wordEntities[id]).filter((word) => holdsValue("wordEntities", word)).sort(compareByOrder);
	const terms = liveIds(doc.termIds ?? [], termEntities).map((id) => termEntities[id]).map((term) => keptDomains.has(term.domainId) ? term : {
		...term,
		domainId: ""
	}).filter((term) => holdsValue("termEntities", term)).sort(compareByOrder);
	const domains = domainIds.map((id) => domainEntities[id]).sort(compareByOrder);
	const writtenDoc = withEntries({
		tableIds,
		relationshipIds,
		indexIds,
		memoIds,
		tableGroupIds
	});
	const writtenCollections = withEntries({
		tableEntities: toRecord(tables),
		tableColumnEntities: toRecord(columns, (a, b) => compare(a.tableId, b.tableId) || compare(a.id, b.id)),
		relationshipEntities: toRecord(relationships),
		indexEntities: toRecord(indexes),
		indexColumnEntities: toRecord(indexColumns, (a, b) => compare(a.indexId, b.indexId) || compare(a.id, b.id)),
		memoEntities: toRecord(memos),
		tableGroupEntities: toRecord(tableGroups),
		wordEntities: writtenRows("wordEntities", words),
		termEntities: writtenRows("termEntities", terms),
		domainEntities: writtenRows("domainEntities", domains)
	});
	return {
		$schema: SCHEMA_URL,
		version: "3.0.0",
		settings: toDocumentSettings(schema.settings),
		doc: writtenDoc ?? {},
		...writtenCollections ? { collections: writtenCollections } : {}
	};
}
function toDocumentJson(schema) {
	return `${stringifyDocument(toDocument(schema))}\n`;
}
//#endregion
export { toDocument, toDocumentJson };
